import Breadcrumb from '@Services/Breadcrumb';
import ResultsInfo from '@Services/ResultsInfo';
import { updateDialog } from '@Containers/Dialogs/DialogsActions';
import { typeOfCopyStructureToQueryModal } from '@Containers/CopyStructureToQueryModal/CopyStructureToQueryModalConstants';
import { structureContainerIds } from '@Pages/StructureEditor/StructureEditorConstants';
import { RXELEMENT_TEXTS } from '@Constants/ElementConstants';
import store from '@Store';
import SearchStorage from '@Services/SearchStorage';
import StructureContainers from '@Services/StructureContainers';
import { generateResultsPath } from '@AppRoutes/utils';
import { getCombinedRouteParams, getTrackingPathName } from '@AppRoutes/AppRoutesSelectors';
import history from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import get from 'lodash/get';
import { dispatchHideGlobalSpinner, dispatchShowGlobalSpinner } from '@Containers/GlobalSpinner/GlobalSpinnerActions';
import { SearchTypes } from '@Constants/CommonConstants';
import * as ResultsDataManage from '@Services/ResultsDataManage';
import { v4 as uuid } from 'uuid';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import extend from 'lodash/extend';
import StructureOptionsData from '@Services/StructureOptionsData';
import { setDrawingFromResults as setDrawingFromResultsAction } from '@Containers/ResultsFilters/ResultsFiltersComponents/StructureFilter/StructureFilterActions';

const { quickSearch: structureUID } = structureContainerIds;

export function _openConfirmationModal(redirectToQuery) {
  store.dispatch(
    updateDialog({
      id: typeOfCopyStructureToQueryModal,
      dialogType: typeOfCopyStructureToQueryModal,
      movable: false,
      backdrop: true,
      closeButton: false,
      className: 'copy-structure-to-query-modal-dialog-wrap',
      redirectToQuery,
    })
  );
}

export function _updateContainerAndStateGo(container) {
  StructureContainers.updateContainer(structureUID, container)
    .then(() =>
      history.push({
        pathname: paths.SEARCH,
        state: { action: 'edit' },
      })
    )
    .catch(Logger.error);
}

export function _redirectToQuery(searchItem, existingContainer) {
  const structureContainer = { data: searchItem.structure, preview: searchItem.preview };

  if (isEmpty(existingContainer.options)) {
    StructureOptionsData.getDefaultOptions()
      .then((options) => {
        structureContainer.options = StructureOptionsData.extractOptionValues(StructureOptionsData.getCheckedOptions(options.groups));
        _updateContainerAndStateGo(structureContainer);
      })
      .catch(Logger.error);
  } else {
    _updateContainerAndStateGo(structureContainer);
  }
}

export function copyToQuery(searchItem) {
  SearchStorage.setNew(searchItem)
    .then(() =>
      StructureContainers.getContainerByUID(structureUID)
        .then((structureContainer) => {
          const redirectToQuery = _redirectToQuery.bind(null, searchItem, structureContainer);

          if (structureContainer.data) {
            try {
              _openConfirmationModal(redirectToQuery);
            } catch (e) {
              Logger.error(e);
            }
          } else {
            redirectToQuery(searchItem, structureContainer);
          }
        })
        .catch(Logger.reject)
    )
    .catch(Logger.error);
}

export function useAsFilter(structure) {
  const { data, preview } = structure;
  store.dispatch(
    setDrawingFromResultsAction({
      preview,
      structure: data,
    })
  );
}

export function goTo(result, query, searchType, originalSearchType, searchId) {
  /*
   * If searchId is provided and search not empty - resolve with provided searchId
   * If searchId is provided but search is missing - SearchStorage.setNew
   * If searchId is not provided - SearchStorage.setNew
   */
  const promise = searchId
    ? SearchStorage.get(searchId).then((search) => {
        if (!isEmpty(search)) {
          return searchId;
        }
        return SearchStorage.setNew({ query, searchType, originalSearchType });
      })
    : SearchStorage.setNew({ query, searchType, originalSearchType });

  promise
    .then((searchIdVal) => {
      const stateParams = {
        databaseId: result.databaseId,
        context: result.context,
        resultName: result.resultName,
        hitSetKey: result.hitSetKey,
        searchId: searchIdVal,
        pageNumber: 1,
        sort: '',
        showStructure: true,
      };

      if (!isUndefined(result.substanceGridView)) {
        extend(stateParams, {
          substanceGridView: result.substanceGridView,
        });
      }

      Breadcrumb.createBreadcrumb(stateParams, result.resultSize)
        .then(() => {
          const pathName = generateResultsPath(stateParams);
          history.push({
            pathname: pathName,
            state: stateParams,
          });
        })
        .catch(Logger.error);
    })
    .catch(Logger.error);
}

export function viewDetails(query, structureId, databaseId, searchParams) {
  const combinedParams = getCombinedRouteParams(store.getState());
  const stateCurrentName = getTrackingPathName(store.getState()) || '';
  // RXFE-9165
  const isViewPreview = stateCurrentName === 'main.search.quick.results';
  const searchId = get(combinedParams, 'searchId');

  // eslint-disable-next-line no-shadow
  const processParams = (searchParams) => {
    if (searchParams.resultSize > 0) {
      // eslint-disable-next-line no-param-reassign
      searchParams.substanceGridView = false;
      return goTo(searchParams, query, SearchTypes.SUBSTANCE_SEARCH, null, isViewPreview && searchId);
    }
    return NotifyDialogsService.openModalWindow({
      title: RXELEMENT_TEXTS.noResultsTitle,
      text: RXELEMENT_TEXTS.noResultsDueToLicense,
      confirmText: RXELEMENT_TEXTS.ok,
      type: 'info',
      backdrop: 'static',
      trackingOptions: {
        component: 'Synthesis planner',
      },
    });
  };

  if (searchParams) {
    return processParams(searchParams);
  }
  dispatchShowGlobalSpinner();

  return ResultsDataManage.getSubstancesById({ structureId, databaseId })
    .then((...args) => {
      dispatchHideGlobalSpinner();

      return processParams(...args);
    })
    .catch(Logger.reject);
}

export function viewAllRelatedSubstances(substanceId, databaseId) {
  return ResultsDataManage.getAllRelatedSubstances({ substanceId, databaseId })
    .then((searchParams) => {
      if (searchParams.resultSize === 0) {
        Logger.warn('No markush related substances found. Possible missing usage of markushStr property');
        return;
      }

      // eslint-disable-next-line no-param-reassign
      searchParams.resetBreadcrumbs = false;
      // eslint-disable-next-line consistent-return
      return goTo(searchParams, SearchTypes.MARKUSH_SUBSTANCES, SearchTypes.MARKUSH_SUBSTANCES);
    })
    .catch(Logger.reject);
}

export function openInDatabase(db, originalSearchType) {
  dispatchShowGlobalSpinner();

  ResultsDataManage.getSubstancesById({ structureId: db.keys[0], databaseId: db.databaseId })
    .then((requestParams) => {
      dispatchHideGlobalSpinner();

      // eslint-disable-next-line no-param-reassign
      requestParams.searchId = requestParams.searchId || uuid();
      // eslint-disable-next-line no-param-reassign
      requestParams.resetBreadcrumbs = false;

      const historyItem = {
        resultName: requestParams.resultName,
        databaseId: requestParams.databaseId,
        context: requestParams.context,
        searchId: requestParams.searchId,
        order: requestParams.order,
        sort: requestParams.sort,
        hitSetKey: requestParams.hitSetKey,
        substanceGridView: false,
      };

      // RXFE-4432: clear info when we switch databases
      ResultsInfo.clearResultsInfo();

      // User can see 2 breadcrumb items but only one will appear on the history page
      // so we need to add low breadcrumb item in history manual according behavior R1

      SearchStorage.set(historyItem.searchId, {
        searchType: SearchTypes.CROSS_LINKING,
        originalSearchType,
      })
        .then(() => {
          // Prepare breadcrumb to make it active on state loaded.
          Breadcrumb.createBreadcrumb(historyItem, requestParams.resultSize)
            .then(() => {
              ResultsDataManage.redirectToResults(requestParams);
            })
            .catch(Logger.error);
        })
        .catch(Logger.error);
    })
    .catch(Logger.error);
}
